import React, { useEffect, useRef, lazy, Suspense, useContext, useState } from 'react';
import Helmet from 'react-helmet';
import * as Sentry from '@sentry/gatsby';

import CssBaseline from '@mui/material/CssBaseline';
import { TestAttributesConfig } from 'react-test-attributes';

import headerTypes from 'mui/assets/types/headerTypes';
import GlobalDSHeader from 'mui/components/layouts/headers/Global_DS_Header';
import Seo from 'components/common/Seo';
import SimpleHeader from 'mui/components/layouts/headers/SimpleHeaders/SimpleHeader';
import SimpleProgressHeader from 'mui/components/layouts/headers/SimpleHeaders/SimpleProgressHeader';
import Main from 'components/common/Main';

import { StepsProgressProvider } from 'components/Booking-new/providers/steps';
import { pushGTMPageView } from 'utils/gtm';
import getPageViewEventProperties from 'utils/getPageViewEventProperties';

import { OpenSansBoldWoff2, OpenSansMediumWoff2, OpenSansExtraBoldWoff2 } from 'mui/assets/fonts';

import getSplitUrlExperiment from '../../../utils/getSplitUrlExperiment';

import { BASContext } from '../../../providers/basContext';
import { appendQueryParams } from '../../../utils/bas/appendQueryParams';
import { filterUTMParams } from '../../../utils/bas/filterUTMparams';

import GlobalStyles from './GlobalStyles';

const DS_Footer = lazy(() => import('mui/components/layouts/DS_Footer'));

function Layout({
  lang,
  allLangLinks,
  canonicalLink,
  noindex,
  pathname,
  pageTitle,
  pageDescription,
  pageKeywords,
  country,
  city,
  coordinates,
  dSHeader,
  simpleFooter,
  image,
  children,
  noPageView,
  redirect,
  noHeadernoFooter,
  postTemplate,
}) {
  const headerRef = useRef(null); // reference element for scroll to top
  const [loadUserCentrics, setLoadUserCentrics] = useState(false);
  const [userCentricsIsLoaded, setUserCentricsIsLoaded] = useState(false);
  const experiment = getSplitUrlExperiment(pathname);
  const ifRouteIsSplitUrlExperiment = !!experiment;

  //if redirect link is from impress, add UTM params if existing
  const buildQueryString = useContext(BASContext);
  const utmParams = filterUTMParams(buildQueryString);

  const redirectURL =
    (redirect?.url?.includes('smile2impress.com') || redirect?.url?.includes('booking.drsmile')) && utmParams
      ? appendQueryParams(redirect.url, utmParams)
      : redirect?.url;

  useEffect(() => {
    if (!process.env.GATSBY_USER_CENTRICS_ID || userCentricsIsLoaded) return;

    const timeOut = setTimeout(() => {
      setLoadUserCentrics(true);
      setUserCentricsIsLoaded(true);
    }, 4000);

    return () => clearTimeout(timeOut);
  }, []);

  useEffect(() => {
    if (noPageView) return;

    const sendPageView = () => {
      const eventProperties = getPageViewEventProperties();
      pushGTMPageView(eventProperties);
    };

    /**
     * Make sure react-helmet is done with it's changes:
     * https://github.com/gatsbyjs/gatsby/commit/42f509eadb06753f7b529f3682f22e012c21dc9b
     */
    requestAnimationFrame(() => {
      requestAnimationFrame(sendPageView);
    });

    return () => cancelAnimationFrame(sendPageView);
  }, []);

  function getHeader(headerType) {
    switch (headerType) {
      case headerTypes.SIMPLE:
        return <SimpleHeader data={dSHeader} headerRef={headerRef} />;
      case headerTypes.SIMPLE_WITHOUT_HOMEPAGE_LINK:
        return <SimpleHeader data={dSHeader} headerRef={headerRef} isHomepageLinkDisabled />;
      case headerTypes.SIMPLE_WITH_PROGRESS:
        return <SimpleProgressHeader headerRef={headerRef} />;
      default:
        return <GlobalDSHeader data={{ ...dSHeader, pathname }} />;
    }
  }

  return (
    <Sentry.ErrorBoundary
      beforeCapture={scope => {
        scope.setTag('pathname', pathname);
        scope.setTag('country', country);
      }}
    >
      <CssBaseline />
      <TestAttributesConfig value={{ suffix: 'test', enableInProductionMode: true }}>
        <Helmet>
          <html lang={lang} />

          {redirect?.url ? <meta httpEquiv="refresh" content={`${redirect.seconds};url=${redirectURL}`} /> : null}

          {allLangLinks &&
            allLangLinks.map((link, index) => (
              <link
                key={`${link.href}-${link.hrefLang}-${index}`}
                href={link.href}
                rel="alternate"
                hrefLang={link.hrefLang}
              />
            ))}

          {canonicalLink && (
            <link
              rel="canonical"
              href={ifRouteIsSplitUrlExperiment ? `https://${experiment?.domain}${experiment?.path}` : canonicalLink}
            />
          )}

          {loadUserCentrics && (
            <script
              key="usercentrics-cmp"
              id="usercentrics-cmp"
              data-settings-id={process.env.GATSBY_USER_CENTRICS_ID}
              src="https://app.usercentrics.eu/browser-ui/latest/loader.js"
              async
            />
          )}
          <link rel="preload" href={OpenSansMediumWoff2} as="font" type="font/woff2" crossOrigin="anonymous" />
          <link rel="preload" href={OpenSansExtraBoldWoff2} as="font" type="font/woff2" crossOrigin="anonymous" />
          <link rel="preload" href={OpenSansBoldWoff2} as="font" type="font/woff2" crossOrigin="anonymous" />
        </Helmet>
        <Seo
          pageTitle={pageTitle}
          pageDescription={pageDescription}
          pageKeywords={pageKeywords}
          ogImage={image}
          country={country}
          city={city}
          coordinates={coordinates}
          pathname={pathname}
          noindex={noindex}
        />
        <GlobalStyles />
        <StepsProgressProvider>
          {!noHeadernoFooter && getHeader(dSHeader.type)}
          <Main postTemplate={postTemplate} simpleHeader={dSHeader.type !== headerTypes.PRIMARY}>
            {children}
          </Main>
          {noHeadernoFooter ? null : (
            <Suspense fallback={<div style={{ height: `300px` }} />}>
              <DS_Footer simpleFooter={simpleFooter} />
            </Suspense>
          )}
        </StepsProgressProvider>
      </TestAttributesConfig>
    </Sentry.ErrorBoundary>
  );
}

export default Layout;
